import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Certif } from './CertificatesPage';
import { getApiV1Url } from './config/api';
import { formatDate } from './utils/dateUtils';

const buildShareLink = (cert: Certif | null) => {
    if (!cert || !cert.cert_reg_number || typeof window === 'undefined') {
        return '';
    }
    const encodedNumber = encodeURIComponent(cert.cert_reg_number);
    return `${window.location.origin}/certificates/${encodedNumber}`;
};

export const SectionRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => {
    if (!value) return null;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: '#0f172a',
                opacity: 0.65,
                fontWeight: 600,
            }}>
                {label}
            </span>
            <span style={{
                fontSize: 16,
                color: '#0f172a',
                fontWeight: 500,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: 1.45,
            }}>
                {value}
            </span>
        </div>
    );
};

export const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
    const childrenArray = React.Children.toArray(children);
    const hasContent = childrenArray.some(child => {
        if (child === null || child === undefined) return false;
        if (typeof child === 'string' && child.trim() === '') return false;
        if (typeof child === 'number' && child === 0) return false;
        return true;
    });
    
    if (!hasContent) {
        return null;
    }
    
    return (
        <div style={{
            background: '#fff',
            border: '1px solid rgba(0,113,188,0.08)',
            borderRadius: 18,
            padding: 28,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            boxShadow: '0 22px 50px rgba(0,113,188,0.08)',
        }}>
            <h3 style={{ margin: 0, fontSize: 16, color: '#111827' }}>{title}</h3>
            <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                {children}
            </div>
        </div>
    );
};

const CertificateDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { certNumber } = useParams<{ certNumber: string }>();

    const decodedNumber = useMemo(() => {
        if (!certNumber) return null;
        return decodeURIComponent(certNumber);
    }, [certNumber]);

    const initialCertState = (location.state as { cert?: Certif } | undefined)?.cert;
    const initialCert = initialCertState && decodedNumber && initialCertState.cert_reg_number === decodedNumber ? initialCertState : null;
    const [cert, setCert] = useState<Certif | null>(initialCert);
    const [loading, setLoading] = useState(!initialCert);
    const [error, setError] = useState<string | null>(null);
    const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');

    useEffect(() => {
        if (!decodedNumber) {
            setError('Некорректный номер сертификата.');
            setLoading(false);
            return;
        }

        if (cert) return;

        let cancelled = false;
        const fetchCert = async () => {
            setLoading(true);
            setError(null);
            try {
                const access = localStorage.getItem('access');
                const response = await axios.get<Certif[]>(getApiV1Url('CERTIFICATES'), {
                    headers: access ? { Authorization: `Bearer ${access}` } : undefined,
                });
                if (cancelled) return;
                const found = (response.data || []).find(item => item.cert_reg_number === decodedNumber);
                if (found) {
                    setCert(found);
                } else {
                    setError('Сертификат не найден. Возможно, данные были обновлены.');
                }
            } catch (err) {
                if (!cancelled) {
                    setError('Ошибка загрузки данных о сертификате.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchCert();
        return () => { cancelled = true; };
    }, [cert, decodedNumber]);

    const shareLink = useMemo(() => buildShareLink(cert), [cert]);

    const handleCopyLink = async () => {
        if (!shareLink) return;
        try {
            await navigator.clipboard.writeText(shareLink);
            setCopyStatus('copied');
            setTimeout(() => setCopyStatus('idle'), 2000);
        } catch {
            setCopyStatus('error');
            setTimeout(() => setCopyStatus('idle'), 2000);
        }
    };

    const handleBack = () => {
        if (location.key !== 'default') {
            navigate(-1);
        } else {
            navigate('/certificates');
        }
    };

    useEffect(() => {
        document.title = 'Обязательный сертификат — АПТС';
    }, []);

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
            }}>
                Загрузка данных сертификата...
            </div>
        );
    }

    if (error || !cert) {
        return (
            <div style={{ padding: 24 }}>
                <div style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    padding: 24,
                    maxWidth: 640,
                    margin: '0 auto',
                }}>
                    <h2 style={{ marginTop: 0 }}>Не удалось открыть сертификат</h2>
                    <p style={{ color: '#4b5563', lineHeight: 1.5 }}>{error || 'Данные отсутствуют.'}</p>
                    <button
                        onClick={() => navigate('/certificates')}
                        style={{
                            padding: '10px 16px',
                            borderRadius: 6,
                            border: '1px solid #d1d5db',
                            background: '#fff',
                            cursor: 'pointer',
                        }}
                    >
                        К списку сертификатов
                    </button>
                </div>
            </div>
        );
    }

    const combineLabs = (field: string) => {
        const labs = [];
        for (let i = 1; i <= 3; i++) {
            const value = (cert as any)[`${field}_${i}`];
            if (value && value !== 'Отсутствует') {
                labs.push(value);
            }
        }
        return labs.length > 0 ? labs.join('; ') : null;
    };

    return (
        <div style={{ padding: '32px 24px', display: 'flex', justifyContent: 'center', background: '#f3f7fb', minHeight: '100vh' }}>
            <div style={{ width: '100%', maxWidth: 1200, display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div style={{
                    background: '#0071bc',
                    borderRadius: 16,
                    padding: 32,
                    color: '#fff',
                    boxShadow: '0 24px 50px rgba(0, 113, 188, 0.35)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 16,
                    alignItems: 'center',
                }}>
                    <button
                        onClick={handleBack}
                        style={{
                            padding: '10px 20px',
                            borderRadius: 999,
                            border: '1px solid rgba(255,255,255,0.4)',
                            background: 'transparent',
                            color: '#fff',
                            cursor: 'pointer',
                            fontWeight: 500,
                        }}
                    >
                        ← Назад
                    </button>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 260 }}>
                        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Обязательный сертификат</h1>
                        <span style={{ fontSize: 16, opacity: 0.95 }}>
                            {cert.cert_reg_number || 'Сертификат без номера'}
                        </span>
                        <span style={{ opacity: 0.9, fontSize: 14 }}>
                            Дата регистрации: {cert.cert_reg_data ? formatDate(cert.cert_reg_data) : 'Не указана'}
                        </span>
                    </div>
                    {shareLink && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
                            <button
                                onClick={handleCopyLink}
                                style={{
                                    padding: '10px 18px',
                                    borderRadius: 999,
                                    border: 'none',
                                    background: '#fff',
                                    color: '#0071bc',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                {copyStatus === 'copied'
                                    ? 'Ссылка скопирована'
                                    : copyStatus === 'error'
                                        ? 'Ошибка копирования'
                                        : 'Скопировать ссылку'}
                            </button>
                        </div>
                    )}
                </div>

                <Section title="Основная информация">
                    <SectionRow label="Номер сертификата" value={cert.cert_reg_number} />
                    {cert.cert_link && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <span style={{
                                fontSize: 11,
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                color: '#0f172a',
                                opacity: 0.65,
                                fontWeight: 600,
                            }}>
                                Ссылка на сертификат
                            </span>
                            <a
                                href={cert.cert_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    fontSize: 16,
                                    color: '#0071bc',
                                    fontWeight: 500,
                                    textDecoration: 'underline',
                                    wordBreak: 'break-word',
                                }}
                            >
                                Открыть сертификат
                            </a>
                        </div>
                    )}
                    <SectionRow label="Статус сертификата" value={cert.cert_status} />
                    <SectionRow label="Схема сертификации" value={cert.cert_schema} />
                    <SectionRow label="Дата регистрации" value={cert.cert_reg_data ? formatDate(cert.cert_reg_data) : null} />
                    <SectionRow label="Дата окончания" value={cert.cert_finish_data ? formatDate(cert.cert_finish_data) : null} />
                    <SectionRow label="Дата остановки" value={cert.cert_stop_data ? formatDate(cert.cert_stop_data) : null} />
                    <SectionRow label="Причина остановки" value={cert.cert_stop_reason} />
                    <SectionRow label="Комментарий АПТС" value={cert.comment_apts} />
                    <SectionRow label="Полный комментарий АПТС" value={cert.full_comment_apts} />
                </Section>

                <Section title="Заявитель">
                    <SectionRow label="Наименование" value={cert.applicant_shortname} />
                    <SectionRow label="Полное наименование" value={cert.applicant_fullname} />
                    <SectionRow label="ИНН" value={cert.applicant_inn} />
                    <SectionRow label="ОГРН" value={cert.applicant_ogrn} />
                    <SectionRow label="Тип" value={cert.applicant_type} />
                    <SectionRow label="Адрес" value={cert.applicant_adres} />
                    <SectionRow label="Регион" value={cert.applicant_region} />
                </Section>

                <Section title="Изготовитель">
                    <SectionRow label="Наименование" value={cert.manufacture_fullname} />
                    <SectionRow label="ИНН" value={cert.manufacture_inn} />
                    <SectionRow label="ОГРН" value={cert.manufacture_ogrn} />
                    <SectionRow label="Адрес" value={cert.manufacture_adres} />
                    <SectionRow label="Место производства" value={cert.manufacture_workplace} />
                    <SectionRow label="Регион" value={cert.region_manufacturer} />
                </Section>

                <Section title="Продукция и документы">
                    <SectionRow label="Продукция" value={cert.product_fullname} />
                    <SectionRow label="Изготовлено в соответствии" value={cert.technical_docs} />
                    <SectionRow label="Проверена на соответствие" value={cert.regulatory_documentation} />
                    <SectionRow label="Импорт продукции" value={cert.product_import} />
                </Section>

                <Section title="Орган по сертификации">
                    <SectionRow label="Наименование" value={cert.os_name} />
                    <SectionRow label="Номер" value={cert.os_number} />
                    <SectionRow label="ID ФСА" value={cert.os_id_fsa} />
                    <SectionRow label="ОГРН" value={cert.os_ogrn} />
                    <SectionRow label="Статус" value={cert.os_status} />
                    <SectionRow label="Дата остановки" value={cert.os_stop_data ? formatDate(cert.os_stop_data) : null} />
                </Section>

                <Section title="Лаборатории">
                    <SectionRow label="Названия лабораторий" value={combineLabs('lan_name')} />
                    <SectionRow label="Номера лабораторий" value={combineLabs('lab_number')} />
                </Section>

                {(cert.smk_cert_number || cert.smk_os_name || cert.smk_os_number) && (
                    <Section title="СМК">
                        <SectionRow label="Номер сертификата СМК" value={cert.smk_cert_number} />
                        <SectionRow label="Номер ОС СМК" value={cert.smk_os_number} />
                        <SectionRow label="Наименование ОС СМК" value={cert.smk_os_name} />
                    </Section>
                )}

                {(cert.cert_docs || cert.cert_violation || cert.lab_violation || cert.os_violation || cert.smk_violation) && (
                    <Section title="Дополнительная информация">
                        <SectionRow label="Документы сертификата" value={cert.cert_docs} />
                        <SectionRow label="Нарушения сертификата" value={cert.cert_violation} />
                        <SectionRow label="Нарушения лаборатории" value={cert.lab_violation} />
                        <SectionRow label="Нарушения ОС" value={cert.os_violation} />
                        <SectionRow label="Нарушения СМК" value={cert.smk_violation} />
                    </Section>
                )}
            </div>
        </div>
    );
};

export default CertificateDetailPage;

